import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Slider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { usePedidoStore } from '../../stores/pedido.store';
import { CarrinhoItem } from '../CarrinhoItem';
import { useAuth } from '@/hooks/useAuth';
import { resolverLimitesDesconto } from '../../services/desconto.helper';

// AIDEV-NOTE: Helpers de formatacao consistentes com o restante do modulo
const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
function formatCurrency(value: number): string {
  return brlFormatter.format(value);
}

interface Step3CarrinhoManagedProps {
  descontoGeral: number;
  onDescontoGeralChange: (v: number) => void;
}

// AIDEV-NOTE: Step 3 "Managed" — exibe apenas a lista de itens (scrollavel) e o painel de totais
// com desconto geral. Condicao de pagamento, observacao e origem foram movidos para Step4.
// Estado de desconto geral e mantido no PedidoWizard pai para persistir entre navegacoes.

export function Step3CarrinhoManaged({
  descontoGeral,
  onDescontoGeralChange
}: Step3CarrinhoManagedProps) {
  const { user } = useAuth();
  const clienteSelecionado = usePedidoStore((s) => s.clienteSelecionado);
  // AIDEV-NOTE: Limites de desconto — politica do cliente (se houver) substitui a do JWT
  const limites = resolverLimitesDesconto(
    user,
    clienteSelecionado?.limite_desconto_especial ?? null
  );
  const podeDescontoTotal = limites.podeDescontoTotal;
  const limiteDesconto = limites.limiteDescontoTotal;
  const podeDescontoItem = limites.podeDescontoItem;
  const limiteDescontoItem = limites.limiteDescontoItem;
  const podeAlterarPreco = user?.pode_alterar_preco_unitario ?? false;
  const limiteReducaoPreco = user?.limite_reducao_preco_pct ?? null;
  const limiteAumentoPreco = user?.limite_aumento_preco_pct ?? null;

  const itens = usePedidoStore((s) => s.itens);
  const precosOriginais = usePedidoStore((s) => s.precosOriginais);
  const resetPedido = usePedidoStore((s) => s.resetPedido);

  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // AIDEV-NOTE: Calculo do subtotal (soma dos totais de cada item com desconto individual)
  const subtotal = useMemo(
    () =>
      itens.reduce(
        (acc, item) =>
          acc + item.valor_unitario * item.quantidade * (1 - (item.desconto_percentual ?? 0) / 100),
        0
      ),
    [itens]
  );

  const descontoTotalValor = subtotal * (descontoGeral / 100);
  const totalFinal = subtotal - descontoTotalValor;

  const handleLimparCarrinho = useCallback(() => {
    resetPedido();
    setClearConfirmOpen(false);
  }, [resetPedido]);

  const handleDescontoSlider = useCallback(
    (_: Event, value: number | number[]) => {
      onDescontoGeralChange(value as number);
    },
    [onDescontoGeralChange]
  );

  const handleDescontoInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v) && v >= 0 && v <= limiteDesconto) {
        onDescontoGeralChange(v);
      }
    },
    [onDescontoGeralChange]
  );

  // AIDEV-NOTE: Layout flex column com altura total do slot do wizard.
  // A lista de itens (flex: 1) rola; o painel de totais fica fixo na parte inferior.
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Cabecalho fixo */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 3 },
          pt: 2,
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}
      >
        <Typography variant="h5">
          Carrinho
          {itens.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({itens.length} {itens.length === 1 ? 'item' : 'itens'})
            </Typography>
          )}
        </Typography>
        {itens.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setClearConfirmOpen(true)}
          >
            Limpar
          </Button>
        )}
      </Box>

      {itens.length === 0 ? (
        <Box sx={{ px: { xs: 1.5, sm: 3 } }}>
          <Alert severity="info">
            Nenhum item no carrinho. Volte ao catalogo para adicionar produtos.
          </Alert>
        </Box>
      ) : (
        <>
          {/* Lista de itens — ROLAVEL */}
          {/* AIDEV-NOTE: overscrollBehavior:contain evita que scroll vaze ao atingir o limite da lista */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              px: { xs: 1.5, sm: 3 },
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <Paper variant="outlined">
              {itens.map((item, idx) => (
                <CarrinhoItem
                  key={`${item.produto_codigo}-${item.cor}-${item.tamanho}-${idx}`}
                  item={item}
                  index={idx}
                  podeDescontoItem={podeDescontoItem}
                  limiteDescontoItem={limiteDescontoItem}
                  podeAlterarPreco={podeAlterarPreco}
                  precoOriginal={precosOriginais[idx]}
                  limiteReducaoPreco={limiteReducaoPreco}
                  limiteAumentoPreco={limiteAumentoPreco}
                />
              ))}
            </Paper>
          </Box>

          {/* Painel de totais — FIXO na parte inferior */}
          <Box sx={{ flexShrink: 0, px: { xs: 1.5, sm: 3 }, pb: 2, pt: 1 }}>
            <Divider sx={{ mb: 1.5 }} />

            {/* Desconto geral — oculto se usuario nao pode dar desconto total */}
            {podeDescontoTotal && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Desconto Geral (%)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Slider
                    value={descontoGeral}
                    onChange={handleDescontoSlider}
                    min={0}
                    max={limiteDesconto}
                    step={0.5}
                    valueLabelDisplay="auto"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    value={descontoGeral}
                    onChange={handleDescontoInput}
                    inputProps={{
                      min: 0,
                      max: limiteDesconto,
                      step: 0.5,
                      style: { textAlign: 'center', width: 50 }
                    }}
                    sx={{ width: 90 }}
                  />
                </Box>
              </Box>
            )}

            {/* Totais */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
              </Box>
              {descontoGeral > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="error.main">
                    Desconto geral ({descontoGeral}%)
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    - {formatCurrency(descontoTotalValor)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  pt: 1,
                  borderTop: '2px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Total
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                  {formatCurrency(totalFinal)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </>
      )}

      {/* Dialog confirmacao limpar carrinho */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <DialogTitle>Limpar carrinho</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja remover todos os {itens.length} itens do carrinho? Esta acao nao pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleLimparCarrinho} color="error" variant="contained">
            Limpar tudo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
